/**
 * PreviewStep - Preview and edit classified entries for Bank Entry workflow
 */

import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useBankEntryStore } from '../../stores/bankEntryStore';
import { LedgerCombobox } from './LedgerCombobox';
import { PatternLearningDialog } from './PatternLearningDialog';
import {
  Table,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Save,
  RefreshCw,
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL;

export const PreviewStep: React.FC = () => {
  const {
    rows,
    updateRow,
    updateMultipleRows,
    totalRows,
    matchedRows,
    llmClassifiedRows,
    unmatchedRows,
    setCurrentStep,
    selectedCompany,
    selectedBankAccount,
    fileName,
    allLedgers,
    setAllLedgers,
    setLoading,
    isLoading,
    pageSize,
    currentPage,
    setPageSize,
    setCurrentPage,
    setJobId,
    // Pattern learning
    pendingEdit,
    showPatternDialog,
    setShowPatternDialog,
    setPendingEdit,
    triggerLedgerEdit,
  } = useBankEntryStore();
  const token = localStorage.getItem('auth_token');
  const { toast } = useToast();
  const fetchingRef = useRef(false); // Prevent race condition

  // Pagination calculations
  const totalPages = Math.ceil(rows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, rows.length);
  const paginatedRows = rows.slice(startIndex, endIndex);

  // Fetch all ledgers for dropdown (only once, with race condition prevention)
  useEffect(() => {
    const fetchLedgers = async () => {
      if (allLedgers.length > 0 || fetchingRef.current) return;
      fetchingRef.current = true;
      try {
        const response = await fetch(
          `${API_BASE}/api/bank-entry/ledgers?company=${encodeURIComponent(selectedCompany)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Failed to fetch ledgers');
        const data = await response.json();
        setAllLedgers(data || []);
      } catch (error) {
        console.error('Error fetching ledgers:', error);
        fetchingRef.current = false; // Reset on error to allow retry
      }
    };
    if (selectedCompany) fetchLedgers();
  }, [selectedCompany, token, setAllLedgers, allLedgers.length]);

  // Check MCP connection and reconnect if needed
  const ensureMcpConnection = async (): Promise<boolean> => {
    try {
      // Try to fetch companies as a connection test
      const response = await fetch(`${API_BASE}/api/bank-entry/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) return true;

      // If 400 with "No MCP connection", try to reconnect via chat session
      if (response.status === 400) {
        toast({
          title: 'Reconnecting to Tally...',
          description: 'MCP connection expired. Attempting to reconnect.',
        });

        // Create a new chat session to re-establish MCP connection
        const reconnectResponse = await fetch(`${API_BASE}/api/chat/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: 'Bank Entry Reconnect',
          }),
        });

        if (reconnectResponse.ok) {
          // Wait a bit for MCP to initialize
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Verify connection is now working
          const verifyResponse = await fetch(`${API_BASE}/api/bank-entry/companies`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (verifyResponse.ok) {
            toast({
              title: 'Reconnected',
              description: 'Successfully reconnected to Tally.',
            });
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      console.error('MCP connection check failed:', error);
      return false;
    }
  };

  const handleCreateVouchers = async () => {
    setLoading(true);

    try {
      // Check MCP connection before submitting job
      const isConnected = await ensureMcpConnection();
      if (!isConnected) {
        toast({
          title: 'Connection Error',
          description: 'Could not connect to Tally. Please refresh and try again.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Submit job to background queue
      const response = await fetch(`${API_BASE}/api/bank-entry/create-vouchers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company: selectedCompany,
          bankLedger: selectedBankAccount,
          fileName: fileName || undefined,
          vouchers: rows.map(r => ({
            rowIndex: r.rowIndex,
            date: r.date,
            narration: r.narration,
            debit: r.debit,
            credit: r.credit,
            ledger: r.ledger,
            vchType: r.vchType,
          })),
        }),
      });

      if (!response.ok) throw new Error('Failed to submit job');

      const data = await response.json();
      console.log('[BankEntry] Job submitted:', data);

      // Store job ID and go to processing step
      setJobId(data.job_id);
      setCurrentStep('processing');

      toast({
        title: 'Processing started',
        description: data.message,
      });
    } catch (error: any) {
      console.error('Error submitting job:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle pattern learning dialog apply
  const handlePatternApply = (rowIndices: number[], pattern: string) => {
    if (!pendingEdit) return;

    updateMultipleRows(rowIndices, {
      ledger: pendingEdit.newValue,
      confidence: 'high', // User verified
    });

    setPendingEdit(null);
    setShowPatternDialog(false);

    toast({
      title: 'Pattern learned',
      description: pattern
        ? `Updated ${rowIndices.length} row(s) and saved pattern "${pattern}"`
        : `Updated ${rowIndices.length} row(s)`,
    });
  };

  const handlePatternDialogClose = () => {
    setPendingEdit(null);
    setShowPatternDialog(false);
  };

  // Get the row being edited for the dialog
  const editedRow = pendingEdit
    ? rows.find(r => r.rowIndex === pendingEdit.rowIndex)
    : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Table className="w-5 h-5" />
              Preview & Edit
            </CardTitle>
            <CardDescription>
              Review and edit the classified entries before creating vouchers
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="text-green-600">
              {matchedRows} pattern matched
            </Badge>
            <Badge variant="outline" className="text-blue-600">
              {llmClassifiedRows} AI classified
            </Badge>
            <Badge variant="outline" className="text-yellow-600">
              {unmatchedRows} unclassified
            </Badge>
            <Badge variant="secondary">
              {totalRows} total
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Pagination controls - top */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show:</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{endIndex} of {rows.length} entries
          </div>
        </div>

        {/* Data table */}
        <div className="border rounded-lg overflow-auto max-h-[500px]">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="p-2 text-left w-12">#</th>
                <th className="p-2 text-left w-28">Date</th>
                <th className="p-2 text-left">Narration</th>
                <th className="p-2 text-right w-24">Debit</th>
                <th className="p-2 text-right w-24">Credit</th>
                <th className="p-2 text-left w-32">Vch Type</th>
                <th className="p-2 text-left w-48">Ledger</th>
                <th className="p-2 text-center w-20">Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row) => (
                <tr key={row.rowIndex} className="border-t hover:bg-muted/50">
                  <td className="p-2">{row.rowIndex + 1}</td>
                  <td className="p-2">{row.date}</td>
                  <td className="p-2 max-w-xs truncate" title={row.narration}>
                    {row.narration}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {row.debit ? row.debit.toLocaleString() : '-'}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {row.credit ? row.credit.toLocaleString() : '-'}
                  </td>
                  <td className="p-2">
                    <Select
                      value={row.vchType}
                      onValueChange={(v) => updateRow(row.rowIndex, { vchType: v as any })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Payment">Payment</SelectItem>
                        <SelectItem value="Receipt">Receipt</SelectItem>
                        <SelectItem value="Contra">Contra</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-2">
                    <LedgerCombobox
                      value={row.ledger}
                      onChange={(v) => triggerLedgerEdit(row.rowIndex, v)}
                      ledgers={allLedgers}
                      placeholder="Search ledger..."
                    />
                  </td>
                  <td className="p-2 text-center">
                    {row.confidence === 'high' ? (
                      <span title="Pattern matched">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" />
                      </span>
                    ) : row.confidence === 'medium' ? (
                      <span title="AI classified - verify">
                        <AlertCircle className="w-4 h-4 text-blue-500 mx-auto" />
                      </span>
                    ) : (
                      <span title="Unclassified">
                        <AlertCircle className="w-4 h-4 text-red-500 mx-auto" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination controls - bottom */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm px-4">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>
        )}

        <div className="flex justify-between mt-6">
          <Button variant="outline" onClick={() => setCurrentStep('select')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button onClick={handleCreateVouchers} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create {totalRows} Vouchers
              </>
            )}
          </Button>
        </div>

        {/* Pattern Learning Dialog */}
        {editedRow && pendingEdit && (
          <PatternLearningDialog
            open={showPatternDialog}
            onClose={handlePatternDialogClose}
            editedRow={editedRow}
            newLedger={pendingEdit.newValue}
            allRows={rows}
            selectedCompany={selectedCompany}
            onApply={handlePatternApply}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default PreviewStep;
